import React, { useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { ROLE_PRIVILEGES, SET_ROLE_PRIVILEGE } from '../../graphql/privileges';

const ROLE_COLUMNS = [
  { key: 'ADMIN', label: 'Admin' },
  { key: 'PROPERTY_MANAGER', label: 'Property Mgr' },
  { key: 'HOUSEKEEPER_MANAGER', label: 'Housekeeper Mgr' },
  { key: 'HOUSEKEEPER', label: 'Housekeeper' },
  { key: 'INSPECTOR', label: 'Inspector' },
  { key: 'MAINTENANCE', label: 'Maintenance' },
  { key: 'GUEST_APP_MANAGER', label: 'Guest App Mgr' },
  { key: 'CALL_CENTER', label: 'Call Center' },
  { key: 'USER', label: 'User' },
];

const PRIVILEGE_ROWS = [
  {
    key: 'TEAM_AVAILABILITY',
    label: 'Team Availability',
    description:
      'Provides access to the Team Availability page, Team Status dashboard widget, personal weekly schedule, and My Availability controls.',
  },
  {
    key: 'TEAM_MEMBER_STATUS',
    label: 'Team Member Status',
    description:
      'Shows the Dashboard presence widget so the user can check in / check out and have their status auto-updated by GPS events.',
  },
];

const buildLookup = (rows) => {
  const lookup = new Set();
  if (!rows) return lookup;
  rows.forEach((row) => {
    lookup.add(`${row.role}::${row.privilegeKey}`);
  });
  return lookup;
};

function PrivilegesSection() {
  const { data, loading, error } = useQuery(ROLE_PRIVILEGES, {
    fetchPolicy: 'cache-and-network',
  });
  const [setRolePrivilege, { loading: saving }] = useMutation(SET_ROLE_PRIVILEGE, {
    refetchQueries: ['RolePrivileges', 'MyPrivileges'],
  });

  const grantedLookup = useMemo(() => buildLookup(data?.rolePrivileges), [data]);

  const handleToggle = async (role, privilegeKey, nextGranted) => {
    try {
      await setRolePrivilege({
        variables: { input: { role, privilegeKey, granted: nextGranted } },
        optimisticResponse: { setRolePrivilege: true },
      });
    } catch (err) {
      toast.error(`Failed to update privilege: ${err.message}`);
    }
  };

  if (loading && !data) {
    return (
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="text-gray-500">Loading privileges...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="text-red-600">Error loading privileges: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Role Privileges</h2>
        <p className="mt-1 text-sm text-gray-600">
          Toggle which user roles can see and use specific features. Changes take effect
          immediately and are scoped per role.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 border-r border-gray-200"
              >
                Privilege
              </th>
              {ROLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-600 whitespace-nowrap"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {PRIVILEGE_ROWS.map((row) => (
              <tr key={row.key} className="hover:bg-gray-50">
                <td className="sticky left-0 z-10 bg-white px-4 py-3 text-sm border-r border-gray-200 align-top min-w-[220px]">
                  <div className="font-medium text-gray-900">{row.label}</div>
                  {row.description && (
                    <div className="text-xs text-gray-500 mt-1 max-w-xs">
                      {row.description}
                    </div>
                  )}
                </td>
                {ROLE_COLUMNS.map((col) => {
                  const isGranted = grantedLookup.has(`${col.key}::${row.key}`);
                  const inputId = `priv-${col.key}-${row.key}`;
                  return (
                    <td key={col.key} className="px-3 py-3 text-center align-middle">
                      <label
                        htmlFor={inputId}
                        className="inline-flex items-center justify-center cursor-pointer"
                      >
                        <input
                          id={inputId}
                          type="checkbox"
                          checked={isGranted}
                          disabled={saving}
                          onChange={(event) =>
                            handleToggle(col.key, row.key, event.target.checked)
                          }
                          aria-label={`${row.label} for ${col.label}`}
                          className="h-5 w-5 rounded border-gray-300 text-sky-600 focus:ring-sky-500 disabled:opacity-50 cursor-pointer"
                        />
                      </label>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 sm:px-6 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg text-xs text-gray-500">
        Tip: a checkbox is checked when that role has the privilege granted. New
        privileges added by engineering will appear automatically as additional rows.
      </div>
    </div>
  );
}

export default PrivilegesSection;
